import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect, createContext, useContext } from 'react';
import {
  ChartBar, Upload, Users,
  Building2, LogOut, ChevronDown, Menu, X, Clock, ExternalLink,
  CreditCard, Fuel, Boxes, Scale, Receipt, Tags, Tag, UserSquare2, ShieldCheck, Gauge, Map as MapIcon,
  Briefcase, PanelLeftClose, PanelLeftOpen, Headset, UsersRound, Network, Radio, Mail, SlidersHorizontal, PhoneForwarded,
  PhoneOutgoing, MapPinned, CalendarClock, Activity, Home, FileText, Factory, Database, Weight, Trash2,} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { startTelemetry, trackPage } from '../../services/telemetry';
import { moduleAccess } from './moduleAccess';
import { c, z } from '../../theme';
import athensLogoWhite from '../../assets/images/Athens-Logo-White.png';

// Collapsed state reaches NavItem and Section without threading a prop through
// every call site -- there are ~30 of them and each one would be a chance to
// miss one, which in a nav means an item that silently cannot be reached.
const SidebarCtx = createContext(false);

// Usage analytics (services/telemetry.js): one page view per route shown.
// Here because AppShell renders only for a signed-in user, around every page.
function usePageTracking() {
  const { pathname } = useLocation();
  useEffect(() => { startTelemetry(); }, []);
  useEffect(() => { trackPage(pathname); }, [pathname]);
}

export default function AppShell() {
  usePageTracking();
  const { user, costCenters, isAdmin, isFinanceReviewer, hasOvertimeAccess,
          logout } = useAuth();
  // Who reaches which report is decided in moduleAccess.js, shared with the
  // landing page so the two cannot disagree.
  const access = moduleAccess({ user, costCenters, isAdmin, isFinanceReviewer, hasOvertimeAccess });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Desktop rail. Persisted because it is a workspace preference, not a
  // per-page one -- collapsing it and having every navigation undo that would
  // make it useless. Read lazily so a blocked/absent localStorage (private
  // windows, some managed browsers) degrades to expanded rather than throwing
  // on mount and taking the whole shell down with it.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('sh-sidebar-collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('sh-sidebar-collapsed', collapsed ? '1' : '0'); } catch { /* preference only */ }
  }, [collapsed]);

  // Close the drawer on Escape. Without this the only way out is the
  // overlay or a nav link, which is awkward on a tablet with a keyboard.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setMobileOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  return (
    <SidebarCtx.Provider value={collapsed}>
    <div className={`sh-root${collapsed ? ' sh-collapsed' : ''}`}>
      <style>{css}</style>

      {/* ── Sidebar ─────────────────────────────────────── */}
      <aside className={`sh-sidebar${mobileOpen ? ' sh-sidebar-open' : ''}`}>
        <div className="sh-sidebar-inner">
          {/* Logo */}
          <div className="sh-logo">
            <div className="sh-logo-row">
              <div className="sh-logo-mark">
                <img src={athensLogoWhite} alt="Athens Services" className="sh-logo-img" />
                <div className="sh-logo-sub">Data Portal</div>
              </div>
              {/* Desktop only -- on a narrow screen the drawer is the control
                  and a second one here would fight it. */}
              <button
                type="button"
                className="sh-collapse-btn"
                onClick={() => setCollapsed((v) => !v)}
                aria-expanded={!collapsed}
                aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              >
                {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
              </button>
            </div>
          </div>

          <nav className="sh-nav">
            <Section label="Portal">
              <NavItem to="/home" icon={<Home size={15} />} label="Home" onClick={() => setMobileOpen(false)} />
            </Section>

            {/* Reviewer nav — cost centers (DVPs only, not admin/finance) */}
            {access.morOwn && (
              <Section label="My Dashboards">
                {costCenters.map((cc) => (
                  <NavItem
                    key={cc.id}
                    to={`/review/${cc.id}/latest`}
                    icon={<Building2 size={15} />}
                    label={cc.name}
                    onClick={() => setMobileOpen(false)}
                  />
                ))}
              </Section>
            )}

            {/* Admins and finance reviewers — Periods lives as a tab inside
                MOR Dashboard (see AdminTabs), not its own sidebar link. */}
            {access.morCompany && (
              <Section label="Operations">
                <NavItem to="/admin/company"   icon={<ChartBar size={15} />}        label="MOR Dashboard"       onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Overtime is gated on the per-user cost-center assignment list
                (user_overtime_cost_centers): hasOvertimeAccess is true for
                admin-class roles (bypass) or anyone with at least one
                assignment. Fuel Report and Inventory used to share that gate
                and no longer do — each has its own standalone grant as well,
                so they can be given to somebody who has no business seeing
                Overtime. Hidden entirely otherwise, rather than
                shown-but-empty. */}
            {access.overtime && (
              <Section label="Overtime">
                <NavItem to="/overtime" icon={<Clock size={15} />} label="Overtime" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {access.fuel && (
              <Section label="Fuel">
                <NavItem to="/fuel-report" icon={<Fuel size={15} />} label="Fuel Report" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {(access.inventory || access.inventoryFleetio) && (
              <Section label="Inventory">
                {access.inventory && (
                  <NavItem to="/inventory" icon={<Boxes size={15} />} label="Inventory" onClick={() => setMobileOpen(false)} />
                )}
                {access.inventoryFleetio && (
                  <NavItem to="/inventory-fleetio" icon={<Boxes size={15} />} label="Inventory (Fleetio)" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {/* Gated by a standalone permission (canViewPostCollections),
                the Expense Analysis shape: the report covers every MRF and
                transfer site at once, so it is not cost-center scoped. */}
            {access.postCollections && (
              <Section label="Post Collections">
                <NavItem to="/post-collections" icon={<Factory size={15} />} label="Daily Productivity Report" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Three standalone permissions, the Expense Analysis shape: Soft-Pak
                Reports (canViewSoftpakReports), Scale-Pak (canViewScalePak) and
                Disposal (canViewDisposal), each granted on its own. All cover
                every company, so none is cost-center scoped; the heading shows
                when any is held. */}
            {(access.softpakReports || access.scalePak || access.disposal) && (
              <Section label="Soft-Pak">
                {access.scalePak && (
                  <NavItem to="/scale-pak" icon={<Weight size={15} />} label="Scale-Pak" onClick={() => setMobileOpen(false)} />
                )}
                {access.disposal && (
                  <NavItem to="/disposal" icon={<Trash2 size={15} />} label="Disposal" onClick={() => setMobileOpen(false)} />
                )}
                {access.softpakReports && (
                  <NavItem to="/softpak-reports" icon={<Database size={15} />} label="Soft-Pak Reports" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {/* Two gates: the R&M Dashboard is cost-center scoped (hasFleetDashboardAccess),
                Fleet Reports a standalone permission (canViewFleetReports) spanning every yard. */}
            {(access.fleetDashboard || access.fleetReports) && (
              <Section label="Fleet">
                {access.fleetDashboard && (
                  <NavItem to="/fleet-dashboard" icon={<Gauge size={15} />} label="R&M Dashboard" onClick={() => setMobileOpen(false)} />
                )}
                {access.fleetReports && (
                  <NavItem to="/fleet-reports" icon={<Database size={15} />} label="Fleet Reports" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {access.sales && (
              <Section label="Sales">
                <NavItem to="/sales-report" icon={<Gauge size={15} />} label="Open Market Commercial" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Gated by a standalone permission (canReviewTimeWageAudit),
                independent of role — not tied to cost-center assignment like
                the sections above, since this is HR/legal-exposure-adjacent
                data about named employees rather than a facility-scoped
                operational report. super_admin always has access. */}
            {access.timeWageAudit && (
              <Section label="Compliance">
                <NavItem to="/time-wage-audit" icon={<Scale size={15} />} label="Time and Wage Audit" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Gated by a standalone permission (canViewExpenseAnalysis),
                independent of role — same pattern as Compliance above, and
                likewise not tied to cost-center assignment, since company-wide
                spend isn't a facility-scoped operational report.
                super_admin always has access. City Pricing shares this
                section but its own standalone permission
                (canManagePriceSheets / canViewPriceSheets) — for now only
                super_admin has been granted it. */}
            {(access.expenseAnalysis || access.customerBilling || access.cityPricing) && (
              <Section label="Finance">
                {access.expenseAnalysis && (
                  <NavItem to="/expense-analysis" icon={<Receipt size={15} />} label="Expense Analysis" onClick={() => setMobileOpen(false)} />
                )}
                {/* Its own standalone permission (canViewCustomerBilling). */}
                {access.customerBilling && (
                  <NavItem to="/customer-billing" icon={<FileText size={15} />} label="Customer Billing" onClick={() => setMobileOpen(false)} />
                )}
                {access.cityPricing && (
                  <NavItem to="/pricing" icon={<Tag size={15} />} label="City Pricing" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {/* ASC Dashboard — ONE link. The pages live as tabs inside it
                (AscShell), which is the shape the old app had; nine sidebar
                entries for one application crowded everything else out. The
                link is drawn when the server would serve this person any of
                its pages, by the same rule that gates the routes. Settings is
                configuration, not a page of the dashboard, so it sits under
                Administration with the portal's other admin pages. */}
            {access.contactCenter && (
              <Section label="Contact Center">
                <NavItem to="/contact-center" icon={<Headset size={15} />} label="ASC Dashboard" onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Staffing & Development Tracker — visible to unrestricted roles
                (super_admin/admin, NOT finance_reviewer), HR/Recruiting
                (canViewStaffingTracker), or DVPs via the reused Overtime/Fuel/
                Inventory facility assignment (hasOvertimeAccess). */}
            {(access.staffing || access.hrHeadcount) && (
              <Section label="Staffing">
                {access.staffing && (
                  <NavItem to="/staffing-tracker" icon={<UserSquare2 size={15} />} label="Staffing Tracker" onClick={() => setMobileOpen(false)} />
                )}
                {access.hrHeadcount && (
                  <NavItem to="/hr-headcount" icon={<Users size={15} />} label="HR Headcount" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {/* External links — available to all authenticated roles */}
            <Section label="Links">
              <ExternalNavItem href="https://cloud.samsara.com/signin" icon={<ExternalLink size={15} />} label="Samsara" />
              <ExternalNavItem href="https://dashboard.athensservices.com/#/signin" icon={<ExternalLink size={15} />} label="Tableau" />
              <ExternalNavItem href="https://secure.fleetio.com/login" icon={<ExternalLink size={15} />} label="Fleetio" />
              <ExternalNavItem href="https://wd1-identity.myworkday.com/wday/authgwy/athensservices/upc/login" icon={<ExternalLink size={15} />} label="Workday" />
              <ExternalNavItem href="https://eac.athensservices.com/" icon={<ExternalLink size={15} />} label="Env. Compliance App" />
            </Section>

            {/* Administration, split three ways rather than one list of ten.
                It was one "Administration" section holding uploads, mappings,
                user admin and the access log — collapsed by default, so finding
                anything in it meant expanding a grab-bag and reading.

                The per-item conditions below are unchanged from that version,
                and tests/appShellNav.test.jsx asserts that: same links, same
                people. Section-level gates exist only so an empty heading never
                renders — a section whose every item is hidden must not appear,
                or it advertises a module the reader cannot open. */}
            {/* Widened past super_admin for one item: the Time and Wage upload
                is reachable by anyone with can_review_time_wage_audit, matching
                requireTimeWageAuditAccess on its endpoints. The section-level
                gate exists only so an empty heading never renders, so it has to
                admit anyone who can see at least one item inside. */}
            {(user?.role === 'super_admin' || user?.canReviewTimeWageAudit) && (
              <Section label="Data Uploads" collapsible>
                {user?.role === 'super_admin' && (<>
                <NavItem to="/admin/upload"     icon={<Upload size={15} />}     label="MOR Upload"        onClick={() => setMobileOpen(false)} />
                <NavItem to="/overtime/upload"  icon={<Upload size={15} />}     label="Overtime Upload"   onClick={() => setMobileOpen(false)} />
                {/* The budget sits beside the payroll it is measured against.
                    Super-admin only, matching requireRole on every budget
                    endpoint -- this file sets the target the whole L0 board is
                    read against. */}
                <NavItem to="/admin/overtime-budget" icon={<Upload size={15} />} label="Overtime Budget"   onClick={() => setMobileOpen(false)} />
                <NavItem to="/inventory/upload" icon={<Upload size={15} />}     label="Inventory Upload"  onClick={() => setMobileOpen(false)} />
                {/* Super-admin only, unlike Expense Mappings below: the upload
                    and commit endpoints are requireRole('super_admin'), so
                    showing this to a canViewExpenseAnalysis user would offer a
                    link that can only 403. */}
                <NavItem to="/expense-analysis/upload" icon={<Upload size={15} />} label="Expense Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/admin/staffing-tracker-upload" icon={<Upload size={15} />} label="Staffing Tracker Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/admin/staffing-job-requisitions-upload" icon={<Upload size={15} />} label="Upload Job Requisitions" onClick={() => setMobileOpen(false)} />
                <NavItem to="/admin/hr-headcount-upload" icon={<Upload size={15} />} label="HR Headcount Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/sales-report/upload" icon={<Upload size={15} />} label="Sales Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/customer-billing/upload" icon={<Upload size={15} />} label="Customer Billing Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/post-collections/upload" icon={<Upload size={15} />} label="Post Collections Upload" onClick={() => setMobileOpen(false)} />
                <NavItem to="/post-collections/rules" icon={<SlidersHorizontal size={15} />} label="Post Collections Rules" onClick={() => setMobileOpen(false)} />
                </>)}
                {/* Wider than the rest of this section by design -- see the
                    route comment in App.jsx. */}
                {(user?.role === 'super_admin' || user?.canReviewTimeWageAudit) && (
                  <NavItem to="/time-wage-audit/upload" icon={<Upload size={15} />} label="Time & Wage Upload" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}

            {user?.role === 'super_admin' && (
              <Section label="User Administration" collapsible>
                <NavItem to="/admin/users"      icon={<Users size={15} />}       label="User Management"   onClick={() => setMobileOpen(false)} />
                {/* A record of what named colleagues did. super_admin here, in
                    RequireSuperAdmin on the route, and requireRole on every API
                    endpoint including the export. */}
                <NavItem to="/admin/access-log" icon={<ShieldCheck size={15} />} label="Access Log"        onClick={() => setMobileOpen(false)} />
                {/* Which pages get used. Gated here, in RequireSuperAdmin on
                    the route, and in requireRole on every API endpoint —
                    the page detail names who opened what. */}
                <NavItem to="/admin/usage"      icon={<Activity size={15} />}    label="Usage"             onClick={() => setMobileOpen(false)} />
              </Section>
            )}

            {/* Reference data. Three of these are reachable without
                super_admin, which is why this section has its own gate rather
                than sitting inside one of the two above. */}
            {(user?.role === 'super_admin' || user?.canManageFuelCards || user?.canViewExpenseAnalysis
              || user?.contactCenter?.canManage) && (
              <Section label="Mappings" collapsible>
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/job-profiles" icon={<Briefcase size={15} />} label="Job Profiles" onClick={() => setMobileOpen(false)} />
                )}
                {/* overtime_cost_centers — the one list Overtime, HR Headcount,
                    Staffing, Inventory, Fuel, Expenses, Fleet Dashboard, and
                    Sales all resolve a raw cost center string against. */}
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/cost-centers" icon={<Building2 size={15} />} label="Cost Centers" onClick={() => setMobileOpen(false)} />
                )}
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/inventory-locations" icon={<Boxes size={15} />} label="Inventory Locations" onClick={() => setMobileOpen(false)} />
                )}
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/fleet-yards" icon={<Gauge size={15} />} label="Fleet Yards" onClick={() => setMobileOpen(false)} />
                )}
                {/* Super-admin only, matching requireRole on the mapping
                    endpoints: these files decide which yard every sales line
                    belongs to. */}
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/sales-mappings" icon={<MapIcon size={15} />} label="Sales Mappings" onClick={() => setMobileOpen(false)} />
                )}
                {/* Super-admin only, matching requireRole on the
                    job-profile-categories endpoints: this decides each
                    job profile's Job Requisition Category portal-wide. */}
                {user?.role === 'super_admin' && (
                  <NavItem to="/admin/staffing-job-req-categories" icon={<Briefcase size={15} />} label="Job Req Categories" onClick={() => setMobileOpen(false)} />
                )}
                {/* ASC configuration — thresholds, forecasts, inbox targets,
                    the summary email and the TV displays. It is not a tab on
                    the dashboard: it is how the dashboard is set up, and it
                    follows the manage permission rather than a page grant. */}
                {user?.contactCenter?.canManage && (
                  <NavItem to="/contact-center/settings" icon={<SlidersHorizontal size={15} />} label="ASC Settings" onClick={() => setMobileOpen(false)} />
                )}
                {(user?.role === 'super_admin' || user?.canManageFuelCards) && (
                  <NavItem to="/admin/fuel-cards" icon={<CreditCard size={15} />} label="Fuel Card Mapping" onClick={() => setMobileOpen(false)} />
                )}
                {/* Expense reference data — visible to anyone who can see the
                    Expense Analysis dashboard, since an unmapped item shows up
                    there and this is where it gets fixed. Editing is gated to
                    super_admin server-side. */}
                {(user?.role === 'super_admin' || user?.canViewExpenseAnalysis) && (
                  <NavItem to="/admin/expense-mappings" icon={<Tags size={15} />} label="Expense Mappings" onClick={() => setMobileOpen(false)} />
                )}
              </Section>
            )}
          </nav>

          {/* User footer */}
          <div className="sh-user">
            <div className="sh-user-info">
              <div className="sh-avatar">
                {user?.displayName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div className="sh-user-text">
                <div className="sh-user-name">{user?.displayName}</div>
                <div className="sh-user-role">{user?.role?.replace('_', ' ')}</div>
              </div>
            </div>
            <button className="sh-logout" onClick={logout} title="Sign out" aria-label="Sign out">
              <LogOut size={15} />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Mobile overlay ───────────────────────────────── */}
      {mobileOpen && (
        <div className="sh-overlay" onClick={() => setMobileOpen(false)} />
      )}

      {/* ── Main content ─────────────────────────────────── */}
      <div className="sh-main">
        {/* Mobile top bar */}
        <div className="sh-mobile-bar">
          <button
            className="sh-menu-btn"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
          <img src={athensLogoWhite} alt="Athens Services" className="sh-mobile-logo" />
        </div>

        <div className="sh-content">
          <Outlet />
        </div>
      </div>
    </div>
    </SidebarCtx.Provider>
  );
}

function Section({ label, children, collapsible = false }) {
  const [open, setOpen] = useState(false);
  const collapsed = useContext(SidebarCtx);

  // In the rail there is no label to click and no room for a disclosure, so a
  // collapsible section would hide its items behind a control that is not
  // there. Show them.
  if (!collapsible || collapsed) {
    return (
      <div className="sh-section">
        <div className="sh-section-label">{label}</div>
        {children}
      </div>
    );
  }

  return (
    <div className="sh-section">
      <button
        type="button"
        className="sh-section-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="sh-section-label sh-section-label-inline">{label}</span>
        <ChevronDown size={12} className={`sh-chevron${open ? ' sh-chevron-open' : ''}`} />
      </button>
      {open && children}
    </div>
  );
}

function NavItem({ to, icon, label, onClick, end = true }) {
  const collapsed = useContext(SidebarCtx);
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      title={collapsed ? label : undefined}
      className={({ isActive }) => `sh-item${isActive ? ' sh-item-active' : ''}`}
    >
      <span className="sh-icon">{icon}</span>
      <span className="sh-item-label">{label}</span>
    </NavLink>
  );
}

function ExternalNavItem({ href, icon, label }) {
  const collapsed = useContext(SidebarCtx);
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="sh-item"
       title={collapsed ? label : undefined}>
      <span className="sh-icon">{icon}</span>
      <span className="sh-item-label">{label}</span>
    </a>
  );
}

// Real CSS, not inline style objects. The previous version could not express
// media queries, which is why the mobile bar was stuck at display:none and
// the hamburger it contained was unreachable — mobileOpen could never become
// true, leaving the drawer and overlay as dead code.
const css = `
.sh-root {
  display: flex;
  min-height: 100vh;
  background: ${c.canvas};
}

/* ── Sidebar ────────────────────────────────────────────────── */
.sh-sidebar {
  width: 240px;
  /* The rail is a width change, so it needs its own transition -- the mobile
     query below overrides this with transform, which is what matters there.
     prefers-reduced-motion kills both. */
  transition: width 0.18s ease;
  flex-shrink: 0;
  background: ${c.navy};
  display: flex;
  flex-direction: column;
  position: sticky;
  top: 0;
  height: 100vh;
  z-index: ${z.sidebar};
}
.sh-sidebar-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding-bottom: 12px;
}

.sh-logo {
  padding: 20px 18px 16px;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  margin-bottom: 8px;
  flex-shrink: 0;
}
.sh-logo-img { height: 22px; width: auto; display: block; }
.sh-logo-row { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; }
.sh-collapse-btn {
  background: none;
  border: none;
  color: ${c.onNavy};
  opacity: 0.55;
  cursor: pointer;
  display: flex;
  padding: 3px;
  border-radius: 6px;
  flex-shrink: 0;
}
.sh-collapse-btn:hover { opacity: 1; background: rgba(255,255,255,0.08); }
.sh-collapse-btn:focus-visible { outline: 2px solid ${c.brand}; outline-offset: 1px; }
/* The drawer has its own hamburger; two controls for one sidebar is one too
   many, and this is the one with no room. */
@media (max-width: 900px) { .sh-collapse-btn { display: none; } }
.sh-logo-sub {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${c.onNavyFaint};
  margin-top: 8px;
}

/* Only the nav scrolls, so the logo and the user footer stay put. */
.sh-nav {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px 10px;
  scrollbar-width: thin;
  scrollbar-color: rgba(255,255,255,0.18) transparent;
}
.sh-nav::-webkit-scrollbar { width: 8px; }
.sh-nav::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.16);
  border-radius: 4px;
}
.sh-nav::-webkit-scrollbar-track { background: transparent; }

.sh-section { margin-bottom: 18px; }
.sh-section-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.15em;
  color: ${c.onNavyLabel};
  padding: 0 8px;
  margin-bottom: 5px;
}
.sh-section-label-inline { margin-bottom: 0; }
.sh-section-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-bottom: 5px;
  color: inherit;
  font-family: inherit;
}
.sh-section-toggle:hover .sh-section-label { color: ${c.onNavyFaint}; }
.sh-chevron {
  opacity: 0.6;
  flex-shrink: 0;
  margin-right: 6px;
  color: ${c.onNavyLabel};
  transform: rotate(-90deg);
  transition: transform 0.15s ease;
}
.sh-chevron-open { transform: rotate(0deg); }

/* ── Nav items ──────────────────────────────────────────────── */
/* The active marker is a red rail rather than only a background tint —
   it survives at a glance and ties the shell to the brand. */
.sh-item {
  position: relative;
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 8px 7px 11px;
  border-radius: 6px;
  font-size: 13px;
  color: ${c.onNavyMuted};
  text-decoration: none;
  margin-bottom: 1px;
  transition: background 0.14s ease, color 0.14s ease;
}
.sh-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 18px;
  border-radius: 0 2px 2px 0;
  background: ${c.brand};
  transition: transform 0.14s ease;
}
.sh-item:hover {
  background: rgba(255,255,255,0.06);
  color: ${c.onNavy};
}
.sh-item-active {
  background: rgba(255,255,255,0.10);
  color: ${c.onNavy};
  font-weight: 500;
}
.sh-item-active::before { transform: translateY(-50%) scaleY(1); }
.sh-item:focus-visible {
  outline: 2px solid ${c.brand};
  outline-offset: 1px;
}
.sh-icon { opacity: 0.85; flex-shrink: 0; display: flex; }
.sh-item-active .sh-icon { opacity: 1; }
/* Cost-center names can be long; truncate rather than wrap to two lines. */
.sh-item-label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── User footer ────────────────────────────────────────────── */
.sh-user {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px 0;
  margin: 0 10px;
  border-top: 1px solid rgba(255,255,255,0.08);
  flex-shrink: 0;
}
.sh-user-info { display: flex; align-items: center; gap: 9px; min-width: 0; }
.sh-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: rgba(255,255,255,0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  color: ${c.onNavy};
  flex-shrink: 0;
}
.sh-user-text { min-width: 0; }
.sh-user-name {
  font-size: 12px;
  font-weight: 500;
  color: ${c.onNavy};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sh-user-role {
  font-size: 10px;
  color: ${c.onNavyFaint};
  text-transform: capitalize;
}
.sh-logout {
  background: none;
  border: none;
  color: ${c.onNavyLabel};
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  flex-shrink: 0;
  transition: background 0.14s ease, color 0.14s ease;
}
.sh-logout:hover { background: rgba(255,255,255,0.08); color: ${c.onNavy}; }
.sh-logout:focus-visible { outline: 2px solid ${c.brand}; outline-offset: 1px; }

/* ── Main ───────────────────────────────────────────────────── */
.sh-main { flex: 1; display: flex; flex-direction: column; min-width: 0; }
.sh-content { padding: 28px; flex: 1; min-width: 0; }

.sh-overlay {
  position: fixed;
  inset: 0;
  background: rgba(10,16,28,0.55);
  z-index: ${z.overlay};
}

/* Hidden on desktop; the media query below is what makes it real. Its
   absence is what broke the mobile nav in the inline-style version. */
.sh-mobile-bar {
  display: none;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background: ${c.navy};
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.sh-menu-btn {
  background: none;
  border: none;
  color: ${c.onNavy};
  cursor: pointer;
  display: flex;
  padding: 4px;
  border-radius: 6px;
}
.sh-menu-btn:focus-visible { outline: 2px solid ${c.brand}; outline-offset: 1px; }
.sh-mobile-logo { height: 18px; width: auto; }

/* ── Collapsed rail (desktop only) ──────────────────────────── */
/* Deliberately inside a min-width query. Below 900px the sidebar is a
   drawer that is either open or off-screen, and a 64px rail there would be
   a third state nobody asked for -- worse, a user who collapsed it on a
   laptop would find a mangled drawer on their phone. The preference is
   remembered either way; it simply does not apply until there is room. */
@media (min-width: 901px) {
  .sh-collapsed .sh-sidebar { width: 64px; }
  .sh-collapsed .sh-logo { padding: 20px 0 16px; }
  .sh-collapsed .sh-logo-mark,
  .sh-collapsed .sh-item-label,
  .sh-collapsed .sh-section-label,
  .sh-collapsed .sh-user-text,
  .sh-collapsed .sh-logout span { display: none; }

  .sh-collapsed .sh-logo-row { justify-content: center; }
  .sh-collapsed .sh-section { margin-top: 6px; }
  /* The label is gone, so the only thing separating one group from the next
     is this line. Without it the rail is an undifferentiated column of ~20
     icons. */
  .sh-collapsed .sh-section + .sh-section {
    border-top: 1px solid rgba(255,255,255,0.08);
    padding-top: 6px;
  }
  .sh-collapsed .sh-item,
  .sh-collapsed .sh-logout {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }
  .sh-collapsed .sh-icon { margin: 0; }
  .sh-collapsed .sh-user { justify-content: center; }
  .sh-collapsed .sh-user-info { justify-content: center; }
  /* The active marker is a 3px bar keyed to the left edge; at 64px wide with
     centred icons it reads as a stray tick unless it spans the full height. */
  .sh-collapsed .sh-item-active::before { top: 0; bottom: 0; }
}

/* ── Narrow screens ─────────────────────────────────────────── */
@media (max-width: 900px) {
  .sh-mobile-bar { display: flex; }
  .sh-sidebar {
    position: fixed;
    left: 0;
    top: 0;
    bottom: 0;
    height: 100%;
    transform: translateX(-100%);
    transition: transform 0.22s ease;
  }
  /* Shadow only while open — on a closed drawer it bleeds a grey seam down
     the left edge of the content. */
  .sh-sidebar-open {
    transform: translateX(0);
    box-shadow: 0 0 40px rgba(0,0,0,0.4);
  }
  .sh-content { padding: 18px 16px; }
}

@media (prefers-reduced-motion: reduce) {
  .sh-sidebar { transition: none; }
}
`;
